import * as React from 'react';
import CombinedBlurPaint from './combinedBlurPaint';
import AlphaRatio from './alphaRatio';
import type { LinearLightCaptureBounds } from './source';
import type { PreparedCombinedBlur } from './prepareCombinedBlurSource';

// SourceGraphic is the live page backdrop, not the captured foreground. Keep
// the blend reference blurred too, so blending cannot restore sharp page
// detail. Preserve paint alpha through blur: transparent fringe pixels must
// reveal the blurred backdrop rather than blend the backdrop with itself.
// Final opacity fades the complete composite once.
export default function CombinedBlurFilter({
  mask,
  paintMask,
  paintMaskOffsetRef,
  backdropOffsetRef,
  backdropInput = 'SourceGraphic',
  separateBlendAlpha = false,
  prepared,
  preparedSize,
  paintOffsetRef,
  offsetRef,
  blur,
  feather,
  blendMode,
  opacity,
  bounds,
}: {
  mask?: string;
  paintMask?: string;
  paintMaskOffsetRef?: React.RefObject<SVGFEOffsetElement>;
  backdropOffsetRef?: React.RefObject<SVGFEOffsetElement>;
  backdropInput?: string;
  separateBlendAlpha?: boolean;
  prepared?: PreparedCombinedBlur;
  preparedSize?: { width: number; height: number };
  paintOffsetRef?: React.RefObject<SVGFEOffsetElement>;
  offsetRef?: React.RefObject<SVGFEOffsetElement>;
  blur: number;
  feather: number;
  blendMode: string;
  opacity: number;
  bounds?: LinearLightCaptureBounds;
}) {
  return (
    <>
      <feImage
        href={mask}
        x={bounds?.x ?? 0}
        y={bounds?.y ?? 0}
        width={bounds?.width ?? '100%'}
        height={bounds?.height ?? '100%'}
        preserveAspectRatio="none"
        result={offsetRef && !prepared ? 'captured-footprint' : 'footprint'}
      />
      {offsetRef && !prepared && (
        <feOffset
          ref={offsetRef}
          in="captured-footprint"
          result="footprint"
          dx="0"
          dy="0"
        />
      )}
      {paintMask && (
        <>
          <feImage
            href={paintMask}
            x={bounds?.x ?? 0}
            y={bounds?.y ?? 0}
            width={bounds?.width ?? '100%'}
            height={bounds?.height ?? '100%'}
            preserveAspectRatio="none"
            result={
              paintMaskOffsetRef && !prepared
                ? 'captured-paint-footprint'
                : 'paint-footprint'
            }
          />
          {paintMaskOffsetRef && !prepared && (
            <feOffset
              ref={paintMaskOffsetRef}
              in="captured-paint-footprint"
              dx="0"
              dy="0"
              result="paint-footprint"
            />
          )}
        </>
      )}
      {prepared ? (
        <>
          <feImage
            href={prepared.paint}
            x="0"
            y="0"
            width={preparedSize?.width}
            height={preparedSize?.height}
            preserveAspectRatio="none"
            result="prepared-paint"
          />
          <feOffset
            ref={paintOffsetRef}
            in="prepared-paint"
            result="blurred"
            dx="0"
            dy="0"
          />
          <feImage
            href={prepared.footprint}
            x="0"
            y="0"
            width={preparedSize?.width}
            height={preparedSize?.height}
            preserveAspectRatio="none"
            result="prepared-footprint"
          />
          <feOffset
            ref={offsetRef}
            in="prepared-footprint"
            result="soft-footprint"
            dx="0"
            dy="0"
          />
        </>
      ) : (
        <CombinedBlurPaint
          blur={blur}
          feather={feather}
          paintFrame={Boolean(paintMask)}
        />
      )}
      <feGaussianBlur
        in={backdropInput}
        stdDeviation={blur}
        result={paintMask ? 'full-blurred-backdrop' : 'blurred-backdrop'}
      />
      {paintMask && (
        <>
          {prepared ? (
            <>
              <feImage
                href={prepared.backdrop}
                width={preparedSize?.width}
                height={preparedSize?.height}
                preserveAspectRatio="none"
                result="prepared-backdrop-ratio"
              />
              <feOffset
                ref={backdropOffsetRef}
                in="prepared-backdrop-ratio"
                dx="0"
                dy="0"
                result="backdrop-ratio"
              />
            </>
          ) : (
            <AlphaRatio
              numerator={feather > 0 ? 'soft-footprint' : 'footprint'}
              denominator={
                feather > 0 ? 'soft-paint-footprint' : 'paint-footprint'
              }
              result="backdrop-ratio"
            />
          )}
          <feComposite
            in="full-blurred-backdrop"
            in2="backdrop-ratio"
            operator="in"
            result="covered-backdrop"
          />
          <feBlend
            in="covered-backdrop"
            in2={backdropInput}
            mode="normal"
            result="blurred-backdrop"
          />
        </>
      )}
      {blendMode === 'linear-light' ? (
        <>
          <feComponentTransfer in="blurred" result="opaque-blurred">
            <feFuncA type="linear" slope="0" intercept="1" />
          </feComponentTransfer>
          <feComponentTransfer in="blurred-backdrop" result="opaque-backdrop">
            <feFuncA type="linear" slope="0" intercept="1" />
          </feComponentTransfer>
          <feComposite
            in="opaque-blurred"
            in2="opaque-backdrop"
            operator="arithmetic"
            k2="2"
            k3="1"
            k4="-1"
            result="linear-blend"
          />
          <feComposite
            in="linear-blend"
            in2="blurred"
            operator="in"
            result="covered-blend"
          />
          <feComposite
            in="covered-blend"
            in2="blurred-backdrop"
            operator="in"
            result="on-backdrop"
          />
          <feComposite
            in="blurred"
            in2="blurred-backdrop"
            operator="out"
            result="off-backdrop"
          />
          <feComposite
            in="on-backdrop"
            in2="off-backdrop"
            operator="arithmetic"
            k2="1"
            k3="1"
            result="linear-layer"
          />
          <feBlend
            in="linear-layer"
            in2="blurred-backdrop"
            mode="normal"
            result="blended"
          />
        </>
      ) : blendMode === 'normal' || !separateBlendAlpha ? (
        <feBlend
          in="blurred"
          in2="blurred-backdrop"
          mode={blendMode}
          result="blended"
        />
      ) : (
        <>
          {/* Blend opaque colour first, then apply intrinsic paint alpha.
              WebKit's non-normal feBlend modes otherwise mishandle alpha. */}
          <feComponentTransfer in="blurred" result="opaque-blurred">
            <feFuncA type="linear" slope="0" intercept="1" />
          </feComponentTransfer>
          <feBlend
            in="opaque-blurred"
            in2="blurred-backdrop"
            mode={blendMode}
            result="blend-colour"
          />
          <feComposite
            in="blend-colour"
            in2="blurred"
            operator="in"
            result="covered-blend"
          />
          <feBlend
            in="covered-blend"
            in2="blurred-backdrop"
            mode="normal"
            result="blended"
          />
        </>
      )}
      <feComponentTransfer
        in={
          prepared
            ? 'soft-footprint'
            : paintMask
            ? feather > 0
              ? 'soft-paint-footprint'
              : 'paint-footprint'
            : feather > 0
            ? 'soft-footprint'
            : 'footprint'
        }
        result="coverage"
      >
        <feFuncA type="linear" slope={opacity} />
      </feComponentTransfer>
      <feComposite in="blended" in2="coverage" operator="in" />
    </>
  );
}
